import classNames from 'classnames';
import React from 'react';

import { formatAccountNumber } from '@eika-infrastruktur/format';
import { RowButton } from '@eika/button';
import { CheckmarkAnimation } from '@eika/checkmark-animation';
import { Loader } from '@eika/loader';

import type { Account } from '../../generated-types';
import { ariaLabelAccount } from '../../services/accessibilityService';
import { AccountLabels } from '../shared/AccountLabels';
import { currencyFormatter } from '../shared/numberFormat';
import { isUnknownAccount } from './Transfer/hooks/useToAccount';

import './AccountListItem.scss';

interface Props {
    account: Account;
    onClick: (account: Account) => void;
    isLoading?: boolean;
    isSuccess?: boolean;
    inactive?: boolean;
    showNegativeAmount?: boolean;
}

const AccountListItem: React.FC<Props> = ({
    account,
    onClick,
    isSuccess,
    isLoading,
    showNegativeAmount = false,
    inactive = false,
}) => {
    const targetBalance = showNegativeAmount ? account.balance.accountingBalance : account.balance.availableBalance;
    return (
        <RowButton
            onClick={() => (isSuccess || isLoading ? null : onClick(account))}
            aria-label={ariaLabelAccount(account)}
            className={classNames('account-list-item__container', {
                'account-list-item__container--inactive': inactive,
            })}
        >
            <>
                <AccountLabels
                    owners={account.owners}
                    className="account-list-item__owner-label"
                    data-testid="account-list-account-owner"
                />
                <div className="account-list-item__row">
                    <span className="account-list-item__alias" data-testid="account-list-name">
                        {account.alias}
                    </span>
                    {isSuccess || isLoading ? (
                        <div className="account-list-item__loading-container">
                            {isSuccess ? <CheckmarkAnimation mini /> : <Loader mini />}
                        </div>
                    ) : (
                        <span className="account-list-item__balance" data-testid="account-list-balance">
                            {!isUnknownAccount(account) && currencyFormatter(targetBalance, account.currency)}
                        </span>
                    )}
                </div>
                <span className="account-list-item__account-number" data-testid="account-list-number">
                    {formatAccountNumber(account.accountNumber)}
                </span>
            </>
        </RowButton>
    );
};

export default AccountListItem;
